// @ts-check
import { adaptPrice } from './priceAdapter';
import { formatCurrency as fmt } from '../utils/currency';

/**
 * @typedef {{
 *   _id: string,
 *   productId: string,
 *   title: string,
 *   image?: string|null,
 *   quantity: number,
 *   price?: import('./priceAdapter').PriceObj|null,
 *   slug?: string,
 * }} RawCartItem
 *
 * @typedef {{
 *   id: string,
 *   productId: string,
 *   title: string,
 *   image: string|null,
 *   slug: string|null,
 *   quantity: number,
 *   priceVM: import('./priceAdapter').PriceViewModel,
 *   lineTotal: string|null,
 * }} CartLineVM

 * @typedef {{
 *   items?: RawCartItem[],
 *   discountTotal?: number,
 *   shippingEstimate?: number,
 *   coupon?: { code: string, discountType: string, discountValue: number }|null,
 * }} RawCart
 *
 * @typedef {{
 *   lines: CartLineVM[],
 *   itemCount: number,
 *   subtotalLabel: string,
 *   subtotalRaw: number|null,
 *   discountRaw: number,
 *   discountLabel: string|null,
 *   shippingRaw: number,
 *   shippingLabel: string,
 *   totalRaw: number|null,
 *   totalLabel: string,
 *   currency: string,
 *   coupon: { code: string, discountType: string, discountValue: number }|null,
 *   hasUnpricedItems: boolean,
 *   canCheckout: boolean,
 * }} CartViewModel
 */



/**
 * Transforms raw cart API response into a fully-typed view model.
 * Runs each item through adaptPrice so the cart UI never touches raw price fields.
 * If ANY item is unpriced: hasUnpricedItems=true AND canCheckout=false.
 *
 * @param {RawCart|null|undefined} rawCart
 * @param {'retail'|'educator'|'school'} [segment]
 * @returns {CartViewModel}
 */
export const adaptCart = (rawCart, segment = 'retail') => {
  const items = rawCart?.items ?? [];

  /** @type {CartLineVM[]} */
  const lines = items.map((item) => {
    const priceVM = adaptPrice(item.price, segment);
    const lineTotal =
      priceVM.raw !== null
        ? fmt(priceVM.raw * item.quantity, priceVM.currency)
        : null;

    return {
      id: item._id,
      productId: item.productId,
      title: item.title,
      image: item.image ?? null,
      slug: item.slug ?? null,
      quantity: item.quantity,
      priceVM,
      lineTotal,
    };
  });

  const hasUnpricedItems = lines.some((l) => !l.priceVM.isPriced);

  const subtotalRaw = hasUnpricedItems
    ? null
    : lines.reduce((acc, l) => acc + (l.priceVM.raw ?? 0) * l.quantity, 0);

  const currency = lines[0]?.priceVM.currency ?? 'USD';
  const discountRaw = Number(rawCart?.discountTotal || 0);
  const shippingRaw = Number(rawCart?.shippingEstimate || 0);
  const totalRaw =
    subtotalRaw !== null
      ? Math.max(0, subtotalRaw - discountRaw + shippingRaw)
      : null;

  const subtotalLabel =
    subtotalRaw !== null ? fmt(subtotalRaw, currency) : 'Price on request';
  const discountLabel = discountRaw > 0 ? `−${fmt(discountRaw, currency)}` : null;
  const shippingLabel = shippingRaw > 0 ? fmt(shippingRaw, currency) : fmt(0, currency);
  const totalLabel = totalRaw !== null ? fmt(totalRaw, currency) : 'Price on request';

  return {
    lines,
    itemCount: items.length,
    subtotalLabel,
    subtotalRaw,
    discountRaw,
    discountLabel,
    shippingRaw,
    shippingLabel,
    totalRaw,
    totalLabel,
    currency,
    coupon: rawCart?.coupon
      ? {
          code: rawCart.coupon.code,
          discountType: rawCart.coupon.discountType,
          discountValue: Number(rawCart.coupon.discountValue || 0),
        }
      : null,
    hasUnpricedItems,
    canCheckout: !hasUnpricedItems && lines.length > 0,
  };
};
